import React, { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { IoMdTime } from "react-icons/io";
import { GoPencil } from "react-icons/go";
import { FaAngleUp, FaAngleDown } from "react-icons/fa6";
import "./CustomTimePicker.css";

export default function CustomTimePicker({ value = "08:00", onChange }) {
  const [open, setOpen] = useState(false);
  const [hours, setHours] = useState(8);      // 1–12
  const [minutes, setMinutes] = useState(0); // 0–59
  const [ampm, setAmpm] = useState("AM");

  useEffect(() => {
    if (!value || !value.includes(":")) return;

    const [h, m] = value.split(":").map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return;

    const isPM = h >= 12;
    const hour12 = h % 12 || 12;

    setHours(hour12);
    setMinutes(m);
    setAmpm(isPM ? "PM" : "AM");
  }, [value]);

  const to24h = (h, m, a) => {
    let hour = h % 12;
    if (a === "PM") hour += 12;
    return `${String(hour).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  };

  const handleSave = () => {
    onChange(to24h(hours, minutes, ampm));
    setOpen(false);
  };

  const increase = (type) => {
    if (type === "hour") {
      setHours((prev) => {
        if (prev === 12 && ampm === "AM") {
          setAmpm("PM");
          return 1;
        }

        if (prev === 11) {
          setAmpm((a) => (a === "AM" ? "PM" : "AM"));
          return 12;
        }

        return prev === 12 ? 1 : prev + 1;
      });
    }

    if (type === "minute") {
      setMinutes((prev) => (prev + 5 >= 60 ? 0 : prev + 5));
    }

    if (type === "ampm") {
      setAmpm((prev) => (prev === "AM" ? "PM" : "AM"));
    }
  };

  const decrease = (type) => {
    if (type === "hour") {
      setHours((prev) => {
        if (prev === 1 && ampm === "PM") {
          setAmpm("AM");
          return 12;
        }

        if (prev === 12) {
          setAmpm((a) => (a === "AM" ? "PM" : "AM"));
          return 11;
        }

        return prev === 1 ? 12 : prev - 1;
      });
    }

    if (type === "minute") {
      setMinutes((prev) => (prev - 5 < 0 ? 55 : prev - 5));
    }

    if (type === "ampm") {
      setAmpm((prev) => (prev === "AM" ? "PM" : "AM"));
    }
  };

  return (
    <div className="appointment-page time-dropdown">
      <div
        className="appointment-page time-input"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="appointment-page icon-block-flex">
          <IoMdTime /> {to24h(hours, minutes, ampm)}
        </span>
        <span className="appointment-page edit-icon">
          <GoPencil />
        </span>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            className="appointment-page timepicker-popup"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
          >
            <div className="timepicker-grid">
              <div className="timepicker-column">
                <button onClick={() => increase("hour")}><FaAngleUp /></button>
                <span className="timepicker-font-weight">
                  {String(hours).padStart(2, "0")}
                </span>
                <button onClick={() => decrease("hour")}><FaAngleDown /></button>
              </div>

              <span className="colon">:</span>

              <div className="timepicker-column">
                <button onClick={() => increase("minute")}><FaAngleUp /></button>
                <span className="timepicker-font-weight">
                  {String(minutes).padStart(2, "0")}
                </span>
                <button onClick={() => decrease("minute")}><FaAngleDown /></button>
              </div>

              <div className="timepicker-column ampm">
                <button onClick={() => increase("ampm")}><FaAngleUp /></button>
                <span className="timepicker-font-weight">{ampm}</span>
                <button onClick={() => decrease("ampm")}><FaAngleDown /></button>
              </div>
            </div>

            <div className="appointment-page time-buttons mt-3">
              <button
                className="appointment-page cancel"
                onClick={() => setOpen(false)}
              >
                Cancel
              </button>
              <button
                className="appointment-page save"
                onClick={handleSave}
              >
                Save
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
